import {Component, computed, EventEmitter, inject, Input, Output, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {GameService} from '../../services/game.service';
import {BggBulkImportItem, BggBulkImportResponse, BggSearchResult} from '../../../../shared/model/bgg';
import {AlertService} from '../../../../shared/services/alert.service';

@Component({
  selector: 'app-bgg-bulk-import',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './bgg-bulk-import.component.html',
  styleUrl: './bgg-bulk-import.component.css'
})
export class BggBulkImportComponent {
  @Output() importCompleted = new EventEmitter<void>();
  @Output() providerChange = new EventEmitter<'bgg' | 'rpg'>();

  @Input() set provider(value: 'bgg' | 'rpg') {
    if (value && value !== this._provider()) {
      this._provider.set(value);
      this.closeSearch();
    }
  }
  get provider(): 'bgg' | 'rpg' {
    return this._provider();
  }

  @Input() showProviderSelector = false;

  private gameService = inject(GameService);
  private alertService = inject(AlertService);

  protected _provider = signal<'bgg' | 'rpg'>('bgg');
  protected query = '';
  protected isSearching = signal(false);
  protected isImporting = signal(false);
  protected searchResults = signal<Array<BggSearchResult>>([]);
  protected selectedIds = signal<Set<number>>(new Set<number>());
  protected lastBulkResult = signal<BggBulkImportResponse | null>(null);

  protected providerTitle = computed(() =>
    this._provider() === 'rpg' ? 'Import da RPGGeek' : 'Import da BoardGameGeek'
  );

  protected placeholderText = computed(() =>
    this._provider() === 'rpg'
      ? 'Cerca su RPGGeek (es: Dungeons & Dragons)'
      : 'Cerca su BGG (es: Catan)'
  );

  protected providerLabel = computed(() =>
    this._provider() === 'rpg' ? 'RPGGeek' : 'BGG'
  );

  protected setProvider(p: 'bgg' | 'rpg'): void {
    if (this._provider() !== p) {
      this._provider.set(p);
      this.providerChange.emit(p);
      this.closeSearch();
    }
  }

  protected search(): void {
    const trimmedQuery = this.query.trim();
    const currentProvider = this._provider();
    const label = this.providerLabel();

    if (!trimmedQuery) {
      this.alertService.showError(`Inserisci un nome gioco per cercare su ${label}`);
      return;
    }

    this.isSearching.set(true);
    this.lastBulkResult.set(null);
    this.selectedIds.set(new Set<number>());

    // Se l'input è numerico, cerca per ID sul provider selezionato
    if (/^\d+$/.test(trimmedQuery)) {
      const gameId = Number(trimmedQuery);
      this.searchById(gameId, currentProvider);
      return;
    }

    // Ricerca testuale solo sul provider selezionato
    this.gameService.searchBggGames(trimmedQuery, currentProvider).subscribe({
      next: (response) => {
        this.isSearching.set(false);
        const results: Array<BggSearchResult> = (response.data ?? []).map(r => ({
          ...r,
          source: currentProvider
        }));
        this.searchResults.set(results);

        if (results.length === 0) {
          this.alertService.showError(`Nessun risultato trovato su ${label}`);
        }
      },
      error: () => {
        this.isSearching.set(false);
        this.alertService.showError(`Errore durante la ricerca su ${label}`);
      }
    });
  }

  private searchById(id: number, currentProvider: 'bgg' | 'rpg'): void {
    const label = this.providerLabel();

    this.gameService.getBggGameDetails(id, currentProvider).subscribe({
      next: (response) => {
        this.isSearching.set(false);
        if (response.data) {
          const gameDto = response.data;
          this.searchResults.set([{
            id: gameDto.id ?? id,
            type: gameDto.type ?? (currentProvider === 'rpg' ? 'rpgitem' : 'boardgame'),
            name: gameDto.names?.[0] ?? { value: 'Sconosciuto' },
            yearPublished: gameDto.yearPublished,
            source: currentProvider
          }]);
        } else {
          this.searchResults.set([]);
          this.alertService.showError(`Nessun gioco trovato con questo ID su ${label}`);
        }
      },
      error: () => {
        this.isSearching.set(false);
        this.alertService.showError(`Nessun gioco trovato con questo ID su ${label}`);
      }
    });
  }

  protected toggleSelection(result: BggSearchResult): void {
    const next = new Set<number>(this.selectedIds());
    if (next.has(result.id)) {
      next.delete(result.id);
    } else {
      next.add(result.id);
    }
    this.selectedIds.set(next);
  }

  protected isSelected(result: BggSearchResult): boolean {
    return this.selectedIds().has(result.id);
  }

  protected importSelected(): void {
    if (this.selectedIds().size === 0) {
      this.alertService.showError('Seleziona almeno un gioco da importare');
      return;
    }

    const currentProvider = this._provider();
    const label = this.providerLabel();

    const payload: Array<BggBulkImportItem> = this.searchResults()
      .filter(result => this.selectedIds().has(result.id))
      .map(result => ({
        bggGameId: result.id,
        gameName: result.name.value,
        source: currentProvider
      }));

    this.isImporting.set(true);
    this.gameService.importBggGamesBulk(payload).subscribe({
      next: (response) => {
        this.lastBulkResult.set(response.data ?? null);
        this.isImporting.set(false);
        this.importCompleted.emit();

        const imported = response.data?.imported ?? 0;
        const skipped = response.data?.skippedExisting ?? 0;
        const failed = response.data?.failed ?? 0;
        this.alertService.showSuccess(`Import completato. Importati: ${imported}, Skippati: ${skipped}, Errori: ${failed}`);
      },
      error: () => {
        this.isImporting.set(false);
        this.alertService.showError(`Errore durante l'import bulk da ${label}`);
      }
    });
  }

  protected resultSource(result: BggSearchResult): string {
    return (result.source ?? this._provider()) === 'rpg' ? 'RPGGeek' : 'BGG';
  }

  protected closeSearch() {
    this.query = '';
    this.searchResults.set([]);
    this.selectedIds.set(new Set<number>());
    this.isSearching.set(false);
  }
}
